import { Alert, Box, Button, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { CycleFormAssignment, CycleStatus, FormGradeField, SaveWeightsBody, Station, StationParam } from '../../types/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { allWeighted, asArray, weightsTotal } from '../../utils/weights';

interface Row { key: string; label: string; weight?: number }
interface Block { id: string; kind: 'form' | 'station'; itemId: string; name: string; rows: Row[] }

/** Initial inputs: stored weights, but only when every row has an integer weight. */
const initialValues = (rows: Row[]): string[] =>
  allWeighted(rows) ? rows.map((r) => String(r.weight)) : rows.map(() => '');

function buildBlocks(assignments: CycleFormAssignment[], stations: Station[]): Block[] {
  const forms = assignments
    .filter((a) => a.formType === 'generic')
    .map((a): Block => ({
      id: `form:${a.id}`,
      kind: 'form',
      itemId: a.id,
      name: a.template?.name ?? '',
      rows: asArray<FormGradeField>(a.structureSnapshot?.structure)
        .filter((f) => f.type === 'gradebox')
        .map((f) => ({ key: f.fieldKey, label: f.title, weight: f.weight })),
    }));
  const sts = stations
    .filter((s) => s.stationType === 'individual')
    .map((s): Block => ({
      id: `station:${s.id}`,
      kind: 'station',
      itemId: s.id,
      name: s.name,
      rows: asArray<StationParam>(s.parameters)
        .filter((p) => p.valueType === 'scale')
        .map((p) => ({ key: p.parameterId, label: p.name, weight: p.weight })),
    }));
  return [...forms, ...sts].filter((b) => b.rows.length > 0);
}

const CycleWeightsCard: React.FC<{ cycleId: string; cycleStatus: CycleStatus }> = ({ cycleId, cycleStatus }) => {
  const { lang } = useDirection();
  const qc = useQueryClient();
  const locked = cycleStatus !== CycleStatus.DRAFT;

  const { data: assignments } = useQuery({
    queryKey: ['formAssignments', cycleId],
    queryFn: () => apiService.getFormAssignmentsByCycle(cycleId),
    enabled: !!cycleId,
  });
  const { data: stations } = useQuery({
    queryKey: ['stations', cycleId],
    queryFn: () => apiService.getStationsByCycle(cycleId),
    enabled: !!cycleId,
  });

  const blocks = React.useMemo(
    () => (assignments && stations ? buildBlocks(assignments, stations) : []),
    [assignments, stations],
  );

  const [values, setValues] = React.useState<Record<string, Record<string, string>>>({});
  const [saved, setSaved] = React.useState(false);

  // Only the user's edits are held here; untouched blocks show the server's weights.
  // Edits are keyed by row key so a refetch that adds, removes or reorders rows cannot misalign them.
  const valuesOf = (b: Block) => {
    const init = initialValues(b.rows);
    const e = values[b.id];
    return e ? b.rows.map((r, i) => e[r.key] ?? init[i]) : init;
  };

  const mutation = useMutation({
    mutationFn: (body: SaveWeightsBody) => apiService.saveCycleWeights(cycleId, body),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['formAssignments', cycleId] });
      qc.invalidateQueries({ queryKey: ['stations', cycleId] });
    },
  });

  const totals = blocks.map((b) => weightsTotal(valuesOf(b)));
  const canSave = totals.every((t) => t.state === 'ok' || t.state === 'empty') && !mutation.isPending;

  const setValue = (b: Block, index: number, v: string) => {
    setSaved(false);
    mutation.reset();
    setValues((prev) => ({ ...prev, [b.id]: { ...prev[b.id], [b.rows[index].key]: v } }));
  };

  const save = () => {
    const body: Required<SaveWeightsBody> = { forms: [], stations: [] };
    blocks.forEach((b, bi) => {
      const weights =
        totals[bi].state === 'empty'
          ? null
          : Object.fromEntries(b.rows.map((r, i) => [r.key, Number(valuesOf(b)[i])]));
      if (b.kind === 'form') body.forms.push({ assignmentId: b.itemId, weights });
      else body.stations.push({ stationId: b.itemId, weights });
    });
    mutation.mutate(body);
  };

  const totalText = (state: string, total: number) => {
    if (state === 'ok') return { text: tr('weights.ok', lang), color: 'success.main' };
    if (state === 'bad') return { text: tr('weights.bad', lang).replace('{n}', String(total)), color: 'error.main' };
    if (state === 'incomplete') return { text: tr('weights.incomplete', lang), color: 'error.main' };
    return { text: tr('weights.empty', lang), color: 'text.secondary' };
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {tr('weights.title', lang)}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 2 }}>{tr('weights.desc', lang)}</Typography>

      {blocks.length === 0 ? (
        <Typography sx={{ fontSize: '0.85rem' }}>{tr('weights.none', lang)}</Typography>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {blocks.map((b, bi) => {
              const t = totalText(totals[bi].state, totals[bi].total);
              return (
                <Box key={b.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '0.8rem', mb: 1 }}>
                    <span>{`${b.name} · ${tr('weights.parameter', lang)}`}</span>
                    <span>{tr('weights.weight', lang)}</span>
                  </Box>
                  {b.rows.map((r, i) => (
                    <Box key={r.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.85rem' }}>{r.label}</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={valuesOf(b)[i]}
                        disabled={locked}
                        onChange={(e) => setValue(b, i, e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 1, dir: 'ltr', 'aria-label': `${b.name} · ${r.label}` }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        sx={{ width: 110 }}
                      />
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{tr('weights.total', lang)}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: t.color }}>{t.text}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {locked ? (
            <Typography sx={{ mt: 2, fontSize: '0.8rem', color: 'text.secondary' }}>{tr('weights.locked', lang)}</Typography>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={save} disabled={!canSave}>{tr('weights.save', lang)}</Button>
              {saved && <Typography sx={{ fontSize: '0.8rem', color: 'success.main' }}>{tr('weights.saved', lang)}</Typography>}
              {mutation.isError && <Alert severity="error" sx={{ py: 0 }}>{getErrorMessage(mutation.error)}</Alert>}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default CycleWeightsCard;
